/* eslint-disable jsx-a11y/media-has-caption */
import * as React from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Avatar, AvatarGroup, Box, IconButton, Stack, Typography, Tooltip,
} from '@mui/material';
import {
  ScreenShareOutlined, Videocam, VideocamOff, Mic, MicOff,
} from '@mui/icons-material';
import {
  ClosableModal, ErrorMessage, PrimaryLoadingButton,
  PrimaryTextField,
  ProgressBar,
  SecondaryButton, VideoPreviewPlayer,
} from 'components';
import { SecondaryBadge } from 'components/molecules/custom-badges';
import useScreenRecorder from 'base/core/hooks/use-screen-recorder';
import JsFileDownloader from 'js-file-downloader';

import {
  CloseSquareFillIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  MaximizeSquareFillIcon,
  MaximizeWebCamIcon,
  MiniimizeSquareFillIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  StopCircleFillIcon,
  StopCircleIcon,
} from 'assets/icons';

import { FileExtensions, UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { dateFormat } from 'utils/formats';
import { useSelector, useDispatch } from 'react-redux';
import { selectSingleConversation, selectUploading } from 'pages/sessions/components/message-hub/selectors';
import { CourseTypes, SessionType } from 'pages/sessions/interface';
import { chatActions } from 'pages/sessions/components/message-hub/slice';
import useFileUpload from 'base/core/hooks/use-file-upload';
import {
  getAvatarLetters, getUserName, replaceExt, retry, sleep,
} from 'utils/common-utils';
import { mediaLibraryAction } from 'pages/media-library/slice';
import { selectEnterprise } from 'base/core/selectors';
import { useConfirmation } from 'base/common';
import './style.scss';
import SelectFolderModel from 'pages/media-library/components/select-folder-model';
import { selectOpenFolderSelect, selectSelectedTargetFolder } from 'pages/media-library/selector';
import { avatarStyle } from '../../styles';

interface Props {
  fullscreen: boolean;
  showSideBar: boolean;
  enterFullScreen: () => void;
  exitFullScreen: () => void;
  toggleSideBar: (isDisplay: boolean) => void;
  container: Element | (() => Element | null) | null | undefined;
  onStartMirroringScreen: () => void;
  onStopMirroringScreen: () => void;
  isMirroringScreen: boolean;
  isPlaying?: boolean;
  setIsPlaying?: (isPlaying: boolean) => void;
}

const TopBar = ({
  fullscreen, showSideBar, enterFullScreen, exitFullScreen, toggleSideBar, container,
  onStartMirroringScreen, onStopMirroringScreen, isMirroringScreen, isPlaying, setIsPlaying,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { conversationId } = useParams();
  const webCamVideoRef = React.useRef<HTMLVideoElement>(null);
  const [webCamFullScreen, setWebCamFullScreen] = React.useState(false);
  const [cameraEnabled, setCameraEnabled] = React.useState(false);
  const [attachSessionError, setAttachSessionError] = React.useState('');
  const [recordFileName, setRecordFileName] = React.useState('');
  const [isRemoteFileDownloading, setIsRemoteFileDownloading] = React.useState(false);

  const { pathname } = useLocation();
  const isRecordOnlyView = React.useMemo(() => pathname === '/media/record-content', [pathname]);

  const isRecordFileNameValid = React.useMemo(() => {
    if (!isRecordOnlyView && recordFileName.length === 0) return { isValid: false, error: '' };
    if (isRecordOnlyView && recordFileName.length < 3) return { isValid: false, error: 'File name should contain atleast 3 characters' };
    if (recordFileName.length > 100) return { isValid: false, error: 'File name should be less than 100 characters' };

    return { isValid: true, error: '' };
  }, [recordFileName, isRecordOnlyView]);

  const {
    users, userSession, session,
  } = useSelector(selectSingleConversation);
  const enterprise = useSelector(selectEnterprise);
  const uploading = useSelector(selectUploading);
  const openFolderSelect = useSelector(selectOpenFolderSelect);
  const { progress, handleContentDataUpload } = useFileUpload();
  const targetFolder = useSelector(selectSelectedTargetFolder);
  let targetPath = '/root';

  if (targetFolder) {
    if (targetFolder.id === '') {
      targetPath = targetFolder.path;
    } else {
      targetPath = `${targetFolder.path}/${targetFolder.title}`;
    }
  }

  const confirm = useConfirmation();

  const remoteContentUrl = React.useRef('');
  const remoteFileName = React.useRef('');

  // handle recording
  const onStartRecording = () => {
    setWebCamFullScreen(false);
  };

  const onStopRecording = () => {};

  const onStartConfirmation = (resolve: any, reject: any) => {
    confirm({
      variant: 'confirm',
      message: 'Would you like to record this in fullscreen mode?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'No',
      containerRef: container,
      catchOnCancel: true,
    }).then(() => {
      toggleSideBar(false);
      enterFullScreen();
      resolve();
    }).catch(() => {
      resolve();
    });
  };

  const {
    mediaBlob,
    mediaBlobUrl,
    pauseRecording,
    resumeRecording,
    startRecording,
    stopRecording,
    status,
    counter,
    beginCounter,
    previewWebcamStream,
    clearBlobUrl,
    muteAudio,
    unMuteAudio,
    isAudioMuted,
  } = useScreenRecorder({
    onStart: onStartRecording, onStop: onStopRecording, onStartConfirmation,
  });

  React.useEffect(() => {
    if (status === 'recording') {
      if (webCamVideoRef.current) {
        webCamVideoRef.current.srcObject = previewWebcamStream;
      }
    } else if (status === 'stopped') {
      exitFullScreen();
      if (setIsPlaying) {
        setIsPlaying(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webCamFullScreen, status]);

  const toggleCamera = () => {
    setCameraEnabled(!cameraEnabled);
  };

  const toggleMicrophone = () => {
    if (isAudioMuted) {
      unMuteAudio();
    } else {
      muteAudio();
    }
  };

  React.useEffect(() => {
    if (status === 'recording' && webCamVideoRef.current) {
      if (cameraEnabled && previewWebcamStream) {
        webCamVideoRef.current.srcObject = previewWebcamStream;
      } else if (!cameraEnabled) {
        webCamVideoRef.current.srcObject = null;
      }
    }
    // Remove previewWebcamStream from dependencies to prevent re-running
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraEnabled, status]);

  const webCamPreviwer = !webCamFullScreen ? (
    cameraEnabled && (
      <div className="webcam-wrapper">
        <div className="webcam-overlay">
          <IconButton onClick={() => { setWebCamFullScreen(true); }}>
            <MaximizeWebCamIcon sx={{ color: 'white' }} />
          </IconButton>
        </div>
        <video
          ref={webCamVideoRef}
          autoPlay
          muted
          style={{
            transition: 'filter 0.3s ease',
            background: '#191919',
          }}
        />
      </div>
    )
  ) : (
    <div className="webcam-full-screen-wrapper">
      <div className="webcam-full-video-wrapper">
        <div className="btn-wrapper">
          <IconButton onClick={() => { setWebCamFullScreen(false); }}>
            <MaximizeSquareFillIcon sx={{ color: 'white' }} />
          </IconButton>
        </div>
        <video
          ref={webCamVideoRef}
          autoPlay
          muted
          style={{
            transition: 'filter 0.3s ease',
            background: '#191919',
          }}
        />
      </div>
    </div>
  );

  // handle exit from digital coaching tool
  const confirmExitHandler = () => {
    if (status === 'recording' || status === 'paused') {
      stopRecording();
    }
    if (isMirroringScreen) {
      onStopMirroringScreen();
    }
    navigate(`/sessions/messages/${conversationId}`);
  };

  const backHandler = () => {
    if (status === 'recording' || status === 'paused') {
      stopRecording();
    }
    if (isMirroringScreen) {
      onStopMirroringScreen();
    }
    navigate(-1);
  };

  const handleExit = (isBack: boolean) => {
    if (status === 'recording' || status === 'paused' || isMirroringScreen) {
      const activeItems = [];
      if (status === 'recording' || status === 'paused') activeItems.push('recording');
      if (isMirroringScreen) activeItems.push('screen mirroring');

      confirm({
        variant: 'confirm',
        icon: 'warning',
        message: `Are you sure you want to exit? This action will discard your active ${activeItems.join(' and ')}.`,
        confirmButtonTxt: 'Discard & Exit',
        cancelButtonTxt: 'Cancel',
        containerRef: container,
      })
        .then(() => {
          if (isBack) {
            backHandler();
          } else {
            confirmExitHandler();
          }
        });
    } else {
      confirm({
        variant: 'confirm',
        icon: 'warning',
        message: 'Are you sure you want to exit?',
        confirmButtonTxt: 'Yes',
        cancelButtonTxt: 'No',
        containerRef: container,
      })
        .then(() => {
          if (isBack) {
            backHandler();
          } else {
            confirmExitHandler();
          }
        });
    }
  };

  const processRecordingFile = async (): Promise<UploadResponse | undefined> => {
    dispatch(chatActions.setStartUploading());
    const blob: any = mediaBlob;
    blob.lastModified = new Date();
    const recordFile = new File([blob], `${uuidv4()}.webm`, {
      type: blob.type,
    });
    const res = await handleContentDataUpload(
      null,
      '',
      UploadScenarios.sessionFeedback,
      recordFile,
    );
    if (res.statusCode === 201) {
      await sleep(5000);
      dispatch(chatActions.setEndUploading());
      return {
        ...res,
        url: res.url.replace('.webm', '.mp4'),
        extension: FileExtensions.mp4.toUpperCase(),
      };
    }
    dispatch(chatActions.setEndUploading());
    return {
      name: '',
      url: '',
      extension: '',
      category: '',
      statusCode: 400,
      message: 'Upload failed',
    };
  };

  const handleAttachToSession = async () => {
    const processResponse: UploadResponse | undefined = await processRecordingFile();
    if (
      processResponse
      && (processResponse.statusCode === 200 || processResponse.statusCode === 201)) {
      await new Promise((resolve, reject) => {
        dispatch(chatActions.sendMessage({
          body: '',
          attachments: [{
            fileUrl: processResponse.url,
            fileName: processResponse.name,
            fileType: processResponse.extension.toUpperCase(),
            fileCategory: processResponse.category || 'video',
            thumbnail: replaceExt(processResponse.url, '.0000001.jpg'),
          }],
          resolve,
          reject,
        }));
      });

      confirm({
        variant: 'info',
        icon: 'success',
        message: 'Your recorded video has been attached to the session. Click OK button to return to the chat',
        confirmButtonTxt: 'Ok',
        containerRef: container,
      }).then(() => {
        confirmExitHandler();
      });
    } else {
      setAttachSessionError('Error while attaching the recording to the session');
    }
  };

  const handleDownloadRecording = async () => {
    const link = document.createElement('a');
    if (!link) return;

    const url = isRecordOnlyView ? remoteContentUrl.current : (mediaBlobUrl as string);
    let filename = '';

    if (isRecordOnlyView) {
      filename = remoteFileName.current;
      setIsRemoteFileDownloading(true);
    } else if (isRecordFileNameValid.isValid) {
      filename = `${recordFileName}.webm`;
    } else {
      filename = `Review content - ${moment().format('MMM_Do_YYYY_hh_mm_A')}.webm`;
    }

    try {
      await retry(() => new JsFileDownloader({ url, filename }), 10, 10000);
    } catch (error) {
      throw new Error('Failed to download the file after 5 retries');
    }
  };

  const handleSaveToMedia = async (isDownload: boolean) => {
    const processResponse: UploadResponse | undefined = await processRecordingFile();
    if (processResponse && processResponse.statusCode === 201) {
      try {
        dispatch(chatActions.setStartUploading());
        await new Promise((resolve, reject) => {
          const fileName = isRecordFileNameValid.isValid
            ? `${recordFileName}`
            : `Review content - ${moment().format('MMM_Do_YYYY_hh_mm_A')}`;

          remoteContentUrl.current = processResponse.url;
          remoteFileName.current = `${fileName}.mp4`;

          dispatch(mediaLibraryAction.uploadMedia({
            path: targetPath,
            expertId: null,
            entId: enterprise.id,
            contentUrl: processResponse.url,
            fileName,
            fileType: processResponse.extension.toUpperCase(),
            fileCategory: processResponse.category ?? null,
            title: fileName,
            description: '',
            isExternalFile: false,
            thumbnailGenerated: '',
            resolve,
            reject,
          }));
        });
        dispatch(chatActions.setEndUploading());
        if (isDownload) {
          await handleDownloadRecording();
          navigate('/media-library/root');
        } else {
          confirm({
            variant: 'info',
            icon: 'success',
            message: 'Your recorded video has been saved to the media',
            confirmButtonTxt: 'Ok',
            blockClosing: true,
            containerRef: container,
          })
            .then(() => {
              navigate('/media-library/root');
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error(error);
        dispatch(chatActions.setEndUploading());
        setAttachSessionError('Error while processing the recorded video');
      }
    } else {
      dispatch(chatActions.setEndUploading());
      setAttachSessionError('Error while saving the recording to the media');
    }
  };

  const handleSaveToMediaClicked = () => {
    dispatch(mediaLibraryAction.setisDownloadMediaEnabled(false));
  };

  const handleSaveToMediaAndDownloadClicked = () => {
    dispatch(mediaLibraryAction.setisDownloadMediaEnabled(true));
  };

  const handleCloseFolderSelect = () => {
    dispatch(mediaLibraryAction.setOpenFolderSelect(false));
  };

  React.useEffect(() => {
    if (conversationId && (session.sessionType !== SessionType.digitalSession
      && session.courseType !== CourseTypes.interactive)) {
      navigate(`/sessions/messages/${conversationId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, navigate]);

  const handleRecordingName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRecordFileName(event.target.value);
  };

  const mirroringScreenConfirm = async () => {
    if (isMirroringScreen) {
      confirm({
        variant: 'confirm',
        message: 'Do you want to stop mirroring your screen?',
        confirmButtonTxt: 'Stop sharing',
        cancelButtonTxt: 'Cancel',
        containerRef: container,
      }).then(() => {
        onStopMirroringScreen();
      });
      return;
    }
    if (fullscreen) {
      confirm({
        variant: 'confirm',
        message: 'This action will exit you from fullscreen. Are you sure you want to mirror the screen?',
        confirmButtonTxt: 'Yes',
        cancelButtonTxt: 'No',
        containerRef: container,
      }).then(() => {
        exitFullScreen();
        setTimeout(async () => {
          await onStartMirroringScreen();
        }, 1000);
      });
    } else {
      await onStartMirroringScreen();
    }
  };

  const handleOnClose = () => {
    clearBlobUrl();
    dispatch(mediaLibraryAction.setSelectedTargetFolder(null));
  };

  const handleDiscard = () => {
    clearBlobUrl();
    dispatch(mediaLibraryAction.setSelectedTargetFolder(null));
  };

  return (
    <Box
      className="row"
      sx={{
        pl: 2, py: 1.5, border: 1, borderColor: 'divider', backgroundColor: 'background.paper',
      }}
    >
      <ClosableModal
        open={status === 'stopped'}
        onClose={handleOnClose}
        title="Your session recording"
        subTitle=""
        modalClass=""
        container={container}
      >
        <>
          <div className="row">
            <div className="col-12 mb-4">
              <VideoPreviewPlayer url={mediaBlobUrl as string} />
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {`If you're happy with your recording, attach it to your ${conversationId ? 'session' : 'media'}.`}
                {!isRecordOnlyView && (` Or, if you want to download your recording and attach it later,
                  use the download button below.`)}
              </Typography>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <PrimaryTextField
                placeholder={`Recording file name ${isRecordOnlyView ? '(required)' : '(optional)'}`}
                onChange={handleRecordingName}
              />
              {!isRecordFileNameValid.isValid && (
                <div className="mt-1 text-center">
                  <ErrorMessage>
                    {isRecordFileNameValid.error}
                  </ErrorMessage>
                </div>
              )}
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <PrimaryLoadingButton
                fullWidth
                onClick={() => {
                  if (isRecordOnlyView) {
                    handleSaveToMediaClicked();
                  } else {
                    handleAttachToSession();
                  }
                }}
                loading={uploading || isRemoteFileDownloading}
                disabled={
                  isRecordOnlyView && (!isRecordFileNameValid.isValid || isRemoteFileDownloading)
                }
              >
                {isRecordOnlyView ? 'Save to my media' : 'Attach to the session'}
              </PrimaryLoadingButton>
            </div>
            <div className="col-12 col-md-6">
              <PrimaryLoadingButton
                fullWidth
                onClick={() => {
                  if (isRecordOnlyView) {
                    handleSaveToMediaAndDownloadClicked();
                  } else {
                    handleDownloadRecording();
                  }
                }}
                loading={uploading || isRemoteFileDownloading}
                disabled={
                  isRecordOnlyView && (!isRecordFileNameValid.isValid || isRemoteFileDownloading)
                }
              >
                {isRecordOnlyView ? 'Save to media and download' : 'Download the recording'}
              </PrimaryLoadingButton>
            </div>
            {attachSessionError !== '' && !uploading && (
              <div className="mt-2 text-center">
                <ErrorMessage>
                  {attachSessionError}
                </ErrorMessage>
              </div>
            )}
            {attachSessionError === '' && uploading && (
              progress > -1 && (
                <Box sx={{ mt: 2 }}>
                  <ProgressBar progress={progress} />
                </Box>
              )
            )}
          </div>
          <div className="row mt-3">
            <div className="col-12 mb-2 mb-md-0">
              <SecondaryButton fullWidth onClick={handleDiscard}>Discard recording</SecondaryButton>
            </div>
          </div>
        </>
      </ClosableModal>
      <div
        className="col-md-12 col-lg-12 col-xl-11"
      >
        <div className="row align-items-center justify-content-between">
          <div className="px-0 col-md-auto d-flex align-items-center">
            <Box
              sx={{
                pt: 2,
                pb: 1.5,
                px: 1,
                border: 1,
                borderTopRightRadius: 6,
                borderBottomRightRadius: 6,
                borderColor: 'divider',
                lineHeight: 1,
                cursor: 'pointer',
                backgroundColor: (theme) => theme.palette.primary.main,
                marginRight: '15px',
                marginLeft: '-18px',
                marginBottom: '-99vh',
                zIndex: 999,
              }}
              onClick={() => toggleSideBar(!showSideBar)}
            >
              {showSideBar ? (
                <DoubleArrowLeftIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              ) : (
                <DoubleArrowRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              )}
            </Box>
            {conversationId && users && users.length > 0 && (
              <>
                <AvatarGroup max={2} sx={{ mr: 1 }}>
                  <Avatar
                    key={users[0]?.id}
                    sx={[avatarStyle]}
                    src={users[0]?.profileImgUrl}
                    alt={getAvatarLetters(users[0])}
                  >
                    {getAvatarLetters(users[0])}
                  </Avatar>
                </AvatarGroup>
                <div>
                  <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1 }}>
                    {`Session with ${getUserName(users[0])}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1, mt: 0.5 }}>
                    {userSession.startAt ? `Session started on ${dateFormat(userSession.startAt)}` : ''}
                  </Typography>
                </div>
                <SecondaryBadge sx={{ ml: 3 }}>
                  {session && session.sessionType ? session.sessionType : ''}
                </SecondaryBadge>
              </>
            )}
          </div>

          <div className="col-md-auto d-flex justify-content-end">

            <Stack direction="row" spacing={1.5}>

              {(conversationId || isRecordOnlyView) && (
                <>
                  <div className="d-flex align-items-center">
                    <div className="mirroring-screen">
                      <IconButton onClick={mirroringScreenConfirm} sx={{ color: isMirroringScreen ? '#FF5858' : 'text.secondary', p: 0 }} disableRipple>
                        <ScreenShareOutlined sx={{ mr: 1 }} />
                        <Typography variant="body2" sx={{ color: 'inherit' }}>
                          Mirroring screen
                        </Typography>
                      </IconButton>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    {status === 'recording' || status === 'paused' ? (
                      <Stack alignItems="center" direction="row" spacing={1} sx={{ color: '#FF5858' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <StopCircleFillIcon sx={{ mr: 1 }} />
                          <Typography variant="body2" sx={{ color: 'inherit', minWidth: '125px' }}>
                            {`Recording ${new Date(counter * 1000).toISOString().substring(11, 11 + 8)}`}
                          </Typography>
                        </Box>
                        {status === 'paused' ? (
                          <Tooltip title="Resume recording">
                            <IconButton sx={{ p: 0 }} onClick={resumeRecording}>
                              <PlayCircleIcon className="cursor-pointer" sx={{ color: '#FF5858' }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Pause recording">
                            <IconButton sx={{ p: 0 }} onClick={pauseRecording}>
                              <PauseCircleIcon className="cursor-pointer" sx={{ color: '#FF5858' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Stop & save recording">
                          <IconButton sx={{ p: 0 }} onClick={stopRecording}>
                            <StopCircleIcon className="cursor-pointer" sx={{ color: '#FF5858' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <div className="record-feedback">
                        <IconButton
                          onClick={() => {
                            if (isPlaying && setIsPlaying) {
                              setIsPlaying(false);
                            }
                            startRecording();
                          }}
                          sx={{ color: 'text.secondary', p: 0 }}
                          disableRipple
                        >
                          <StopCircleFillIcon sx={{ mr: 1 }} />
                          <Typography variant="body2" sx={{ color: 'inherit' }}>
                            Record feedback
                          </Typography>
                        </IconButton>
                      </div>
                    )}
                  </div>
                </>
              )}
              {fullscreen ? (
                <Tooltip title="Exit fullscreen">
                  <IconButton onClick={exitFullScreen} sx={{ p: 0 }}>
                    <MiniimizeSquareFillIcon sx={{ color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Fullscreen">
                  <IconButton onClick={enterFullScreen} sx={{ p: 0 }}>
                    <MaximizeSquareFillIcon sx={{ color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
              {/* <UploadSquareFillIcon sx={{ color: 'text.secondary' }} /> */}
              {conversationId && (
                <Tooltip title="Close and return to chat">
                  <IconButton onClick={() => { handleExit(false); }} sx={{ p: 0 }}>
                    <CloseSquareFillIcon sx={{ color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
              {!conversationId && (
                <Tooltip title="Close and go back">
                  <IconButton onClick={() => { handleExit(true); }} sx={{ p: 0 }}>
                    <CloseSquareFillIcon sx={{ color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

          </div>
        </div>
      </div>
      {status === 'begin' ? (
        <div className="recorderCountDown position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
          <Typography variant="h3" color="white">
            {`Recording will start in ${String(beginCounter).padStart(2, '0')}`}
          </Typography>
        </div>
      ) : null}
      {status === 'recording' ? (
        webCamPreviwer
      ) : null}
      <div className="settings-wrapper d-flex flex-column align-items-center">
        <IconButton onClick={toggleCamera}>
          {cameraEnabled ? (
            <Videocam sx={{ color: '#FF5858', fontSize: '2rem' }} />
          ) : (
            <VideocamOff sx={{ color: '#CDCDDF', fontSize: '2rem' }} />
          )}
        </IconButton>
        <IconButton onClick={toggleMicrophone}>
          {isAudioMuted ? (
            <MicOff sx={{ color: '#CDCDDF', fontSize: '2rem' }} />
          ) : (
            <Mic sx={{ color: '#FF5858', fontSize: '2rem' }} />
          )}
        </IconButton>
      </div>
      <SelectFolderModel
        open={openFolderSelect}
        onClose={handleCloseFolderSelect}
        handleSaveToMedia={handleSaveToMedia}
      />
    </Box>
  );
};

TopBar.defaultProps = {
  isPlaying: false,
  setIsPlaying: () => {},
};

export default TopBar;
